import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import API_CONFIG from "../config/api.js";
import { showApiError, showLoginError } from "../utils/alerts.js";

// Custom base query with error handling
const baseQueryWithErrorHandling = fetchBaseQuery({
  baseUrl: API_CONFIG.BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = localStorage.getItem("token");
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithAlerts = async (args, api, extraOptions) => {
  const result = await baseQueryWithErrorHandling(args, api, extraOptions);
  
  if (result.error) {
    // Handle different types of errors
    const { status, data } = result.error;
    let errorMessage = 'An error occurred';
    
    if (data?.message) {
      errorMessage = data.message;
    } else if (data?.error) {
      errorMessage = data.error;
    }
    
    // Create error object for alert system
    const error = new Error(errorMessage);
    error.status = status;
    
    // Show appropriate alert based on endpoint
    if (args.url && args.url.includes('/auth/login')) {
      showLoginError(error);
    } else {
      const operation = getOperationName(args.url, args.method);
      showApiError(error, operation);
    }
  }
  
  return result;
};

// Helper function to determine operation name from URL and method
const getOperationName = (url, method) => {
  if (!url) return 'operation';
  
  if (url.includes('/employees')) {
    if (method === 'POST') return 'create employee';
    if (method === 'PUT') return 'update employee';
    if (method === 'DELETE') return 'delete employee';
    return 'load employees';
  }
  
  if (url.includes('/shifts')) {
    if (method === 'POST') return 'create shift';
    if (method === 'PUT') return 'update shift';
    if (method === 'DELETE') return 'delete shift';
    return 'load shifts';
  }
  
  if (url.includes('/clockings')) {
    if (method === 'POST') return 'record time entry';
    if (method === 'PUT') return 'update time record';
    if (method === 'DELETE') return 'delete time record';
    return 'load time records';
  }
  
  if (url.includes('/dashboard')) return 'load dashboard';
  if (url.includes('/admins')) return 'manage administrators';
  if (url.includes('/customers')) return 'load customers';
  if (url.includes('/products')) return 'load products';
  
  return 'operation';
};

export const api = createApi({
  baseQuery: baseQueryWithAlerts,
  reducerPath: "adminApi",
  tagTypes: API_CONFIG.CACHE_TAGS,
  endpoints: (build) => ({
    login: build.mutation({
      query: (credentials) => ({
        url: "auth/login",
        method: "POST",
        body: credentials,
      }),
      invalidatesTags: ["Login"],
    }),
    getUser: build.query({
      query: (id) => `general/user/${id}`,
      providesTags: ["User"],
    }),
    getEmployees: build.query({
      query: (id) => `timeManagement/employees`,
      providesTags: ["User"],
    }),
    updateEmployee: build.mutation({
      query: ({ id, userId, ...patch }) => ({
        url: `timeManagement/employees/${id}`,
        method: 'PUT',
        body: { ...patch, userId },
      }),
      invalidatesTags: ["User"],
    }),
    deleteEmployee: build.mutation({
      query: ({ id, userId }) => ({
        url: `timeManagement/employees/${id}`,
        method: 'DELETE',
        body: { userId },
      }),
      invalidatesTags: ["User"],
    }),
    createEmployee: build.mutation({
      query: ({ userId, ...employeeData }) => ({
        url: 'timeManagement/employees',
        method: 'POST',
        body: { ...employeeData, userId },
      }),
      invalidatesTags: ["User"],
    }),
    resetEmployeePin: build.mutation({
      query: ({ id, pin, userId }) => ({
        url: `timeManagement/employees/${id}/reset-pin`,
        method: 'PUT',
        body: { pin, userId },
      }),
    }),
    getAllClockings: build.query({
      query: (id) => `timeManagement/shift-activities/clockings`,
      providesTags: ["User"],
    }),
    getClockingById: build.query({
      query: (id) => `timeManagement/shift-activities/clockings/${id}`,
      providesTags: ["User"],
    }),
    createClocking: build.mutation({
      query: (newClocking) => ({
        url: 'timeManagement/shift-activities/clockings',
        method: 'POST',
        body: newClocking,
      }),
      invalidatesTags: ["User"],
    }),
    updateClocking: build.mutation({
      query: ({ id, ...patch }) => ({
        url: `timeManagement/shift-activities/clockings/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: ["User"],
    }),
    deleteClocking: build.mutation({
      query: ({ id, userId }) => ({
        url: `timeManagement/shift-activities/clockings/${id}`,
        method: 'DELETE',
        body: { userId },
      }),
      invalidatesTags: ["User"],
    }),
    processClocking: build.mutation({
      query: ({ id, ...patch }) => ({
        url: `timeManagement/shift-activities/clockings/${id}/process`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: ["User"],
    }),
    getAllBreaks: build.query({
      query: (id) => `timeManagement/shift-activities/breaks`,
      providesTags: ["User"],
    }),
    getBreakById: build.query({
      query: (id) => `timeManagement/shift-activities/breaks/${id}`,
      providesTags: ["User"],
    }),
    getBreaksByClockingId: build.query({
      query: (clockingId) => `timeManagement/shift-activities/breaks/clocking/${clockingId}`,
      providesTags: ["User"],
    }),
    createBreak: build.mutation({
      query: (newBreak) => ({
        url: 'timeManagement/shift-activities/breaks',
        method: 'POST',
        body: newBreak,
      }),
      invalidatesTags: ["User"],
    }),
    updateBreak: build.mutation({
      query: ({ id, ...patch }) => ({
        url: `timeManagement/shift-activities/breaks/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: ["User"],
    }),
    deleteBreak: build.mutation({
      query: ({ id, userId }) => ({
        url: `timeManagement/shift-activities/breaks/${id}`,
        method: 'DELETE',
        body: { userId },
      }),
      invalidatesTags: ["User"],
    }),
    getWeeklyHoursSummary: build.query({
      query: ({ startDate, endDate } = {}) => {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        return `timeManagement/shift-activities/weekly-summary?${params.toString()}`;
      },
      providesTags: ["User"],
    }),
    getLaborCostingReport: build.query({
      query: ({ startDate, endDate, department } = {}) => {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (department) params.append('department', department);
        return `timeManagement/analytics/labor-costing?${params.toString()}`;
      },
      providesTags: ["User"],
    }),
    getOvertimeExceptionReport: build.query({
      query: ({ startDate, endDate, department } = {}) => {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (department) params.append('department', department);
        return `timeManagement/analytics/overtime-exceptions?${params.toString()}`;
      },
      providesTags: ["User"],
    }),
    getProductivityAttendanceAnalytics: build.query({
      query: ({ startDate, endDate, department } = {}) => {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (department) params.append('department', department);
        return `timeManagement/analytics/productivity-attendance?${params.toString()}`;
      },
      providesTags: ["User"],
    }),
    getAllShifts: build.query({
      query: (id) => `timeManagement/shifts`,
      providesTags: ["Shifts"],
    }),
    createShift: build.mutation({
        query: (newShift) => ({
            url: 'timeManagement/shifts',
            method: 'POST',
            body: newShift,
        }),
        invalidatesTags: ["Shifts"],
    }),
    updateShift: build.mutation({
        query: ({ id, ...patch }) => ({
            url: `timeManagement/shifts/${id}`,
            method: 'PUT',
            body: patch,
        }),
        invalidatesTags: ["Shifts"],
    }),
    deleteShift: build.mutation({
        query: (id) => ({
            url: `timeManagement/shifts/${id}`,
            method: 'DELETE',
        }),
        invalidatesTags: ["Shifts"],
    }),
    getAvailabilitiesByEmployee: build.query({
        query: (employeeId) => `timeManagement/availabilities/employee/${employeeId}`,
        providesTags: ["Availabilities"],
    }),
    getAllAvailabilities: build.query({
        query: () => `timeManagement/availabilities`,
        providesTags: ["Availabilities"],
    }),
    createAvailability: build.mutation({
        query: (newAvailability) => ({
            url: 'timeManagement/availabilities',
            method: 'POST',
            body: newAvailability,
        }),
        invalidatesTags: ["Availabilities"],
    }),
    updateAvailability: build.mutation({
        query: ({ id, ...patch }) => ({
            url: `timeManagement/availabilities/${id}`,
            method: 'PUT',
            body: patch,
        }),
        invalidatesTags: ["Availabilities"],
    }),
    deleteAvailability: build.mutation({
        query: (id) => ({
            url: `timeManagement/availabilities/${id}`,
            method: 'DELETE',
        }),
        invalidatesTags: ["Availabilities"],
    }),
    processAvailability: build.mutation({
        query: ({ id, ...patch }) => ({
            url: `timeManagement/availabilities/${id}/process`,
            method: 'PUT',
            body: patch,
        }),
        invalidatesTags: ["Availabilities"],
    }),
    getTimeOffRequestsByEmployee: build.query({
        query: (employeeId) => `timeManagement/time-off-requests/employee/${employeeId}`,
        providesTags: ["TimeOffRequests"],
    }),
    getAllTimeOffRequests: build.query({
        query: () => `timeManagement/time-off-requests`,
        providesTags: ["TimeOffRequests"],
    }),
    createTimeOffRequest: build.mutation({
        query: (newRequest) => ({
            url: 'timeManagement/time-off-requests',
            method: 'POST',
            body: newRequest,
        }),
        invalidatesTags: ["TimeOffRequests"],
    }),
    updateTimeOffRequest: build.mutation({
        query: ({ id, ...patch }) => ({
            url: `timeManagement/time-off-requests/${id}`,
            method: 'PUT',
            body: patch,
        }),
        invalidatesTags: ["TimeOffRequests"],
    }),
    deleteTimeOffRequest: build.mutation({
        query: ({ id, userId }) => ({
            url: `timeManagement/time-off-requests/${id}`,
            method: 'DELETE',
            body: { userId },
        }),
        invalidatesTags: ["TimeOffRequests"],
    }),
    
    getProducts: build.query({
      query: () => "client/products",
      providesTags: ["Products"],
    }),
    getCustomers: build.query({
      query: () => "client/customers",
      providesTags: ["Customers"],
    }),
    getTransactions: build.query({
      query: ({ page, pageSize, sort, search }) => ({
        url: "client/transactions",
        method: "GET",
        params: { page, pageSize, sort, search },
      }),
      providesTags: ["Transactions"],
    }),
    getGeography: build.query({
      query: () => "client/geography",
      providesTags: ["Geography"],
    }),
    getSales: build.query({
      query: () => "sales/sales",
      providesTags: ["Sales"],
    }),
    getAdmins: build.query({
      query: () => "management/admins",
      providesTags: ["Admins"],
    }),
    getAdminById: build.query({
      query: (id) => `management/admins/${id}`,
      providesTags: ["Admins"],
    }),
    createAdmin: build.mutation({
      query: (adminData) => ({
        url: "management/admins",
        method: "POST",
        body: adminData,
      }),
      invalidatesTags: ["Admins"],
    }),
    updateAdmin: build.mutation({
      query: ({ id, ...patch }) => ({
        url: `management/admins/${id}`,
        method: "PUT",
        body: patch,
      }),
      invalidatesTags: ["Admins"],
    }),
    deleteAdmin: build.mutation({
      query: (id) => ({
        url: `management/admins/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Admins"],
    }),
    getUserPerformance: build.query({
      query: (id) => `management/performance/${id}`,
      providesTags: ["Performance"],
    }),
    getDashboard: build.query({
      query: () => "general/dashboard",
      providesTags: ["Dashboard"],
    }),

    getFootprints: build.query({
      query: () => 'timeManagement/footprints',
      providesTags: ['Footprints'],
    }),
    testFootprint: build.mutation({
      query: () => ({
        url: 'timeManagement/footprints/test',
        method: 'POST',
      }),
      invalidatesTags: ['Footprints'],
    }),
  }),
});

export const {
  useLoginMutation,
  useGetUserQuery,
  useGetEmployeesQuery,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
  useCreateEmployeeMutation,
  useResetEmployeePinMutation,
  useGetAllClockingsQuery,
  useGetClockingByIdQuery,
  useCreateClockingMutation,
  useUpdateClockingMutation,
  useDeleteClockingMutation,
      useProcessClockingMutation,
    useGetAllBreaksQuery,
    useGetBreakByIdQuery,
    useGetBreaksByClockingIdQuery,
    useCreateBreakMutation,
    useUpdateBreakMutation,
    useDeleteBreakMutation,
    useGetWeeklyHoursSummaryQuery,
    useGetLaborCostingReportQuery,
    useGetOvertimeExceptionReportQuery,
    useGetProductivityAttendanceAnalyticsQuery,
    useGetAllShiftsQuery,
  useCreateShiftMutation,
  useUpdateShiftMutation,
  useDeleteShiftMutation,
  useGetAvailabilitiesByEmployeeQuery,
  useGetAllAvailabilitiesQuery,
  useCreateAvailabilityMutation,
  useUpdateAvailabilityMutation,
  useDeleteAvailabilityMutation,
  useProcessAvailabilityMutation,
  useGetTimeOffRequestsByEmployeeQuery,
  useGetAllTimeOffRequestsQuery,
  useCreateTimeOffRequestMutation,
  useUpdateTimeOffRequestMutation,
  useDeleteTimeOffRequestMutation,
  useGetProductsQuery,
  useGetCustomersQuery,
  useGetTransactionsQuery,
  useGetGeographyQuery,
  useGetSalesQuery,
  useGetAdminsQuery,
  useGetAdminByIdQuery,
  useCreateAdminMutation,
  useUpdateAdminMutation,
  useDeleteAdminMutation,
  useGetUserPerformanceQuery,
  useGetDashboardQuery,
  useGetFootprintsQuery,
  useTestFootprintMutation,
} = api;
